import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

import { getErrorMessage } from '@/lib/apiError';
import { StatusEnvio, StatusGlobal, TipoInforme } from '@/types/enums';

import { useReintentarEnvio } from '../hooks/useEnvios';
import type { DestinatarioEnvio, EnvioInforme } from '../types';

interface EnvioRowProps {
  envio: EnvioInforme;
}

function getStatusConfig(status: EnvioInforme['status_global']) {
  switch (status) {
    case StatusGlobal.TODOS_ENVIADOS:
      return {
        icon: <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />,
        label: 'Enviado',
        chipColor: 'success' as const,
        borderColor: 'success.main',
      };
    case StatusGlobal.PENDING:
      return {
        icon: <AccessTimeIcon sx={{ fontSize: 20, color: 'info.main' }} />,
        label: 'Pendiente',
        chipColor: 'info' as const,
        borderColor: 'info.main',
      };
    default:
      return {
        icon: <WarningAmberIcon sx={{ fontSize: 20, color: 'error.main' }} />,
        label: 'Falló',
        chipColor: 'error' as const,
        borderColor: 'error.main',
      };
  }
}

function DestinatarioItem({
  destinatario,
  uidEnvio,
}: {
  destinatario: DestinatarioEnvio;
  uidEnvio: string;
}): JSX.Element {
  const reintentarMutation = useReintentarEnvio();
  const isFailed = destinatario.status === StatusEnvio.FAILED;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {destinatario.destinatario}
        </Typography>
        <Chip
          label={isFailed ? 'Falló' : 'Enviado'}
          size="small"
          color={isFailed ? 'error' : 'success'}
          sx={{ height: 18, fontSize: 10 }}
        />
        {isFailed && (
          <IconButton
            size="small"
            color="error"
            title="Reintentar envío"
            disabled={reintentarMutation.isPending}
            onClick={() => void reintentarMutation.mutateAsync(uidEnvio)}
          >
            {reintentarMutation.isPending ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <RefreshIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        )}
      </Stack>
      {isFailed && destinatario.error_message && (
        <Typography variant="caption" color="error" display="block" sx={{ ml: 0.5 }}>
          {destinatario.error_message}
        </Typography>
      )}
      {reintentarMutation.isError && (
        <Typography variant="caption" color="error" display="block">
          {getErrorMessage(reintentarMutation.error)}
        </Typography>
      )}
    </Box>
  );
}

export function EnvioRow({ envio }: EnvioRowProps): JSX.Element {
  const config = getStatusConfig(envio.status_global);
  const isPending = envio.status_global === StatusGlobal.PENDING;
  const fecha = format(parseISO(envio.fecha_solicitud), 'dd/MM/yyyy HH:mm', { locale: es });
  const tipoLabel = envio.tipo_informe === TipoInforme.FINAL ? 'Final' : 'Parcial';

  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: config.borderColor,
        borderRadius: 1,
        p: '12px 14px',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={envio.destinatarios.length > 0 ? 1 : 0}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {config.icon}
          <Typography variant="subtitle2" fontWeight={600}>
            Informe {tipoLabel}
          </Typography>
          <Chip
            label={config.label}
            size="small"
            color={config.chipColor}
            sx={{ height: 20, fontSize: 11 }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {fecha}
        </Typography>
      </Stack>

      <Stack spacing={0.5} sx={{ ml: 3.5 }}>
        {envio.destinatarios.map((d) =>
          isPending ? (
            <Typography key={d.destinatario} variant="body2" color="text.secondary" noWrap>
              {d.destinatario}
            </Typography>
          ) : (
            <DestinatarioItem key={d.destinatario} destinatario={d} uidEnvio={envio.uid_envio} />
          )
        )}
      </Stack>
    </Box>
  );
}
